import {
  ProductCategory,
  ProductCategoryValidity,
  Simulation,
  UpsellProposal,
} from '~types/appsync.types';
import type { CartProduct } from '~types/cart';
import { Participant } from '~types/participant';
import { formatDateISO } from '~utils';

declare global {
  var dataLayer: Record<string, unknown>[] | undefined;
}

type EcommerceItem = {
  discount: number;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_id: string;
  item_name: string;
  item_variant: string;
  price: number;
  quantity: number;
};

type EcommerceEvent = {
  ecommerce: {
    currency: string;
    items: EcommerceItem[];
    value: number;
  };
  event: string;
};

type EcommerceEventParams = {
  date?: Date;
  participants: Participant[];
  productCategory?: ProductCategory;
  simulation: Simulation;
  validityCategory?: ProductCategoryValidity;
};

type PriceGroup = {
  categoryShortname: string;
  discount: number;
  orderItem: Simulation['orderItems'][0];
  price: number;
  quantity: number;
};

const safeDataLayerPush = (...entries: Record<string, unknown>[]): void => {
  globalThis.dataLayer ??= [];
  try {
    globalThis.dataLayer.push(...entries);
  } catch (err) {
    console.warn('[analytics] dataLayer push failed', err);
  }
};

const pushEcommerceEvent = (
  eventName: string,
  { date, participants, productCategory, simulation, validityCategory }: EcommerceEventParams,
): void => {
  if (simulation.orderItems.length === 0) {
    return;
  }

  const groupsByKey = new Map<string, PriceGroup>();

  participants.forEach((participant) => {
    const categoryShortname = participant.consumerCategory.shortname;
    const orderItem = simulation.orderItems.find((oi) => oi.participantId === participant.id);

    if (!orderItem?.variant) return;

    const price = orderItem.total / 100;
    const discount = (orderItem.maxPublicPrice - orderItem.total) / 100;
    const groupKey = `${categoryShortname}-${price}-${discount}`;

    const existing = groupsByKey.get(groupKey);
    if (existing) {
      existing.quantity += 1;
    } else {
      groupsByKey.set(groupKey, {
        categoryShortname,
        discount,
        orderItem,
        price,
        quantity: 1,
      });
    }
  });

  const items: EcommerceItem[] = [];

  groupsByKey.forEach(({ categoryShortname, discount, orderItem, price, quantity }) => {
    items.push({
      discount,
      item_category: orderItem.type.toLowerCase(),
      item_category2: productCategory?.shortname ?? '',
      item_category3: validityCategory?.shortname ?? '',
      item_category4: String(validityCategory?.value ?? ''),
      item_category5: date ? formatDateISO(date) : '',
      item_id: orderItem.variant.id,
      item_name: orderItem.variant.sku,
      item_variant: categoryShortname.toUpperCase(),
      price,
      quantity,
    });
  });

  if (items.length === 0) return;

  const totalValue = simulation.prices.total / 100;

  const event: EcommerceEvent = {
    ecommerce: {
      currency: simulation.currency,
      items,
      value: totalValue,
    },
    event: eventName,
  };

  safeDataLayerPush({ ecommerce: null }, event);
};

export const pushViewItemEvent = (params: EcommerceEventParams): void => {
  pushEcommerceEvent('view_item', params);
};

export const pushAddToCartEvent = (params: EcommerceEventParams): void => {
  pushEcommerceEvent('add_to_cart', params);
};

type PromotionItem = {
  item_id: string;
  item_name: string;
  price: number;
};

export const pushViewPromotionEvent = (
  proposals: UpsellProposal[],
  sourceProductSlug: string,
): void => {
  const items: PromotionItem[] = proposals.map((p) => ({
    item_id: p.productSlug,
    item_name: p.title,
    price: p.priceDifference / 100,
  }));

  safeDataLayerPush(
    { ecommerce: null },
    {
      ecommerce: { items },
      event: 'view_promotion',
      source_product_slug: sourceProductSlug,
    },
  );
};

export const pushSelectPromotionEvent = (
  proposal: UpsellProposal,
  sourceProductSlug: string,
): void => {
  safeDataLayerPush(
    { ecommerce: null },
    {
      ecommerce: {
        items: [
          {
            item_id: proposal.productSlug,
            item_name: proposal.title,
            price: proposal.priceDifference / 100,
          },
        ],
      },
      event: 'select_promotion',
      source_product_slug: sourceProductSlug,
    },
  );
};

export const pushBeginCheckoutEvent = ({
  products,
  simulation,
}: {
  products: CartProduct[];
  simulation: Simulation;
}): void => {
  if (products.length === 0 || simulation.orderItems.length === 0) return;

  const ownerByGlobalId = new Map<number, { product: CartProduct; participant: Participant }>();
  let globalId = 1;
  products.forEach((product) => {
    product.participants.forEach((participant) => {
      ownerByGlobalId.set(globalId, { participant, product });
      globalId += 1;
    });
  });

  const items: EcommerceItem[] = simulation.orderItems
    .map((orderItem): EcommerceItem | null => {
      const owner = ownerByGlobalId.get(orderItem.participantId);
      if (!owner || !orderItem.variant) return null;

      const consumerShortname = owner.participant.consumerCategory.shortname;
      return {
        discount: (orderItem.maxPublicPrice - orderItem.total) / 100,
        item_category: orderItem.type.toLowerCase(),
        item_category2: owner.product.productCategoryShortname,
        item_category3: owner.product.validityCategoryShortname,
        item_category4: '',
        item_category5: owner.product.date,
        item_id: orderItem.variant.id,
        item_name: orderItem.variant.sku,
        item_variant: consumerShortname.toUpperCase(),
        price: orderItem.total / 100,
        quantity: 1,
      };
    })
    .filter((item): item is EcommerceItem => item !== null);

  if (items.length === 0) return;

  safeDataLayerPush(
    { ecommerce: null },
    {
      ecommerce: {
        currency: simulation.currency,
        items,
        value: simulation.prices.total / 100,
      },
      event: 'begin_checkout',
    },
  );
};
